import { IconPlay } from "@/assets/icons/geist/IconPlay";
import { Button } from "@/components/flexkit/Button";
import { Text } from "@/components/podkit/typography/Text";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { useCallbackPreventDefault } from "@/hooks/use-callback-prevent-default";
import { type PlainWorkflow, useListWorkflowExecutions, useStartWorkflow } from "@/queries/automation-queries";
import { formatError } from "@/utils/errors";
import { getMostRecentExecution, workflowName } from "@/utils/automations";
import { type FC, useMemo } from "react";
import { WorkflowExecutionStatusIcon } from "./AutomationExecutionStatusIcon";
import { WorkflowExecutionStatusText } from "./AutomationExecutionStatusText";
import { cn, type PropsWithClassName } from "@/components/podkit/lib/cn";

type WorkflowCardProps = {
    workflow: PlainWorkflow;
    readonly?: boolean;
} & PropsWithClassName;

export const WorkflowCard: FC<WorkflowCardProps> = ({ workflow, className, readonly = false }) => {
    const startWorkflow = useStartWorkflow();
    const { toast } = useToast();

    const { data: executions } = useListWorkflowExecutions({
        enabled: true,
        workflowId: workflow.id,
    });

    const mostRecentExecution = useMemo(() => getMostRecentExecution(executions), [executions]);

    const onStartWorkflow = useCallbackPreventDefault(
        async (e: React.SyntheticEvent) => {
            e.stopPropagation();
            try {
                await startWorkflow.mutateAsync(workflow.id);
            } catch (error) {
                toast({ title: "Failed to start automation", description: formatError(error) });
            }
        },
        [startWorkflow, workflow.id, toast],
    );

    return (
        <div className={cn("flex flex-col gap-3 rounded-xl border border-border-base p-4", className)}>
            <div>
                <div className="flex items-center justify-between gap-2">
                    <Text className="text-base font-medium text-content-primary">{workflowName(workflow)}</Text>
                    {!readonly && (
                        <Button
                            LeadingIcon={IconPlay}
                            loading={startWorkflow.isPending}
                            size="sm"
                            variant="outline"
                            onClick={onStartWorkflow}
                            data-tracking-id="start-workflow-workflow-card"
                        />
                    )}
                </div>
                <Text className="text-base text-content-strong">{workflow.metadata?.description}</Text>
            </div>
            {mostRecentExecution && (
                <div className="flex items-center gap-1">
                    <WorkflowExecutionStatusIcon execution={mostRecentExecution} />
                    <WorkflowExecutionStatusText execution={mostRecentExecution} />
                </div>
            )}
        </div>
    );
};
